import type { FormField as ApiField } from "@prequel/import-react";
import {
  CopyToClipboard,
  Dropdown,
  type DropdownListItem,
  FormField,
  PasswordDot,
  type RadioCardOption,
  RadioCards,
  TextArea,
  Toggle,
} from "@prequel/shared-components";

import VendorLogo from "@/components/VendorLogo";

type FieldValues = Record<string, unknown>;

const MASKED_INPUT_DOTS = 20;
const MASKED_TEXTAREA_DOTS = 184;

type ApiFormFieldProps = {
  field: ApiField;
  values: FieldValues;
  setFieldValue: (field: ApiField, value: unknown) => void;
  rootKey?: string;
  disabled?: boolean;
  isEditing?: boolean;
};

type ApiEnumObjectWithIcon = NonNullable<ApiField["enum"]>[number] & {
  icon_url?: string;
};

const ApiFormField = ({
  field,
  values,
  setFieldValue,
  rootKey = "source",
  disabled = false,
  isEditing = false,
}: ApiFormFieldProps) => {
  const value = getFieldValue(values, field.path, rootKey);
  const fieldId = field.path;
  const overwriteOnly = isEditing && !!field.write_only;

  if (field.behavior === "display" || field.behavior === "fixed") {
    return <ReadOnlyField field={field} />;
  }

  switch (field.control) {
    case "toggle":
      return (
        <Toggle
          label={field.label}
          checked={value === true}
          setChecked={(checked: boolean) => setFieldValue(field, checked)}
          disabled={disabled}
        />
      );
    case "radio":
      return (
        <RadioField
          field={field}
          label={field.label}
          description={field.description}
          value={value}
          setFieldValue={setFieldValue}
          disabled={disabled}
        />
      );
    case "select":
      return (
        <SelectField
          field={field}
          label={field.label}
          description={field.description}
          value={value}
          setFieldValue={setFieldValue}
          disabled={disabled}
        />
      );
    case "textarea":
    case "code":
      return (
        <TextArea
          id={fieldId}
          label={field.label}
          placeholder={overwriteOnly ? PasswordDot.repeat(MASKED_TEXTAREA_DOTS) : field.placeholder}
          overwriteOnly={overwriteOnly}
          subtext={field.description}
          value={formatValue(value)}
          onChangeHandler={(nextValue) => setFieldValue(field, nextValue)}
          required={field.required}
          disabled={disabled}
        />
      );
    case "password":
    case "number":
    case "text":
    default:
      return (
        <FormField
          id={fieldId}
          label={field.label}
          type={field.control === "number" ? "number" : field.control === "password" ? "password" : "text"}
          placeholder={
            field.control === "password" || overwriteOnly ? PasswordDot.repeat(MASKED_INPUT_DOTS) : field.placeholder
          }
          overwriteOnly={overwriteOnly}
          subtext={field.description}
          value={formatValue(value)}
          onChangeHandler={(nextValue) => setFieldValue(field, nextValue)}
          required={field.required}
          disabled={disabled}
        />
      );
  }
};

type ChoiceFieldProps = {
  field: ApiField;
  label: string;
  description?: string;
  value: unknown;
  setFieldValue: (field: ApiField, value: unknown) => void;
  disabled: boolean;
};

const RadioField = ({ field, label, description, value, setFieldValue, disabled }: ChoiceFieldProps) => {
  const items: RadioCardOption<string>[] =
    field.enum?.map((option) => {
      const iconUrl = (option as ApiEnumObjectWithIcon).icon_url;

      return {
        value: String(option.key),
        label: option.display,
        enabled: !option.disabled,
        icon: iconUrl ? <VendorLogo logo_url={iconUrl} /> : undefined,
      };
    }) ?? [];
  const selectedOption = items.find((item) => item.value === String(value));

  return (
    <RadioCards
      size="md"
      label={label}
      options={items}
      selectedOption={selectedOption}
      setSelectedOption={(option: RadioCardOption<string>) => setFieldValue(field, option.value)}
      disabled={disabled || items.length === 0}
      subtext={description}
    />
  );
};

const SelectField = ({ field, label, description, value, setFieldValue, disabled }: ChoiceFieldProps) => {
  const items: DropdownListItem<string>[] =
    field.enum?.map((option) => {
      const iconUrl = (option as ApiEnumObjectWithIcon).icon_url;

      return {
        key: String(option.key),
        text: option.display,
        unavailable: option.disabled,
        ...(iconUrl ? { icon: () => <VendorLogo logo_url={iconUrl} /> } : {}),
      };
    }) ?? [];
  const selectedItem = items.find((item) => item.key === String(value));

  return (
    <Dropdown
      label={label}
      items={items}
      selectedItem={selectedItem}
      setSelectedItem={(item: DropdownListItem<string>) => setFieldValue(field, item.key)}
      disabled={disabled || items.length === 0}
      subtext={description}
    />
  );
};

function getFieldValue(values: FieldValues, path: string, rootKey: string): unknown {
  const parts = path
    .split(".")
    .filter(Boolean)
    .filter((part, index) => index !== 0 || part !== rootKey);

  return parts.reduce<unknown>((current, key) => {
    if (current == null || typeof current !== "object") {
      return undefined;
    }
    return (current as Record<string, unknown>)[key];
  }, values);
}

const ReadOnlyField = ({ field }: { field: ApiField }) => {
  const displayValue =
    typeof field.const === "object" ? JSON.stringify(field.const, null, 2) : String(field.const ?? "");

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700">{field.label}</label>
      <div
        className="
          mt-1 inline-flex w-full rounded-md border border-gray-300 bg-gray-50 py-2 pr-1 pl-3 font-mono text-sm
          text-gray-500 shadow-sm
        "
      >
        <div className="mr-3 max-h-56 w-full overflow-auto break-all whitespace-pre-wrap">
          {displayValue || <span className="text-gray-400 italic">Not set</span>}
        </div>
        {!!displayValue && <CopyToClipboard textToCopy={displayValue} className="sticky top-0 self-start" />}
      </div>
      {field.description && <p className="mt-2 text-xs whitespace-pre-line text-gray-400">{field.description}</p>}
    </div>
  );
};

function formatValue(value: unknown): string {
  if (value === undefined || value === null) {
    return "";
  }
  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
}

export default ApiFormField;
